import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import ProfileHeader from '../../components/teacher/profile/ProfileHeader';
import AboutSection from '../../components/teacher/profile/AboutSection';
import OverviewSection from '../../components/teacher/profile/OverviewSection';
import QualificationSection from '../../components/teacher/profile/QualificationSection';
import TimeSlotsSection from '../../components/teacher/profile/TimeSlotsSection';
import ReviewsSection from '../../components/teacher/profile/ReviewsSection';
import RecommendationsSection from '../../components/teacher/profile/RecommendationsSection';
import AnimatedBlobBackground from '../../components/common/backgrounds/AnimatedBlobBackground';
import TeacherStepperModal from '../../components/teacher/modals/TeacherStepperModal';
import * as teacherService from '../../services/teacher.service';
import { fetchTeacherFees } from '../../services/teacherFee.service';
import useAuth from '../../hooks/useAuth';
import { mapProfileToTeacher } from '../../utils/teacherMapper';

export default function TeacherProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [teacher, setTeacher] = useState(null);
  const [teacherFees, setTeacherFees] = useState([]);
  const [feesLoading, setFeesLoading] = useState(false);
  const [feesError, setFeesError] = useState('');
  const [showStepper, setShowStepper] = useState(false);
  const [loading, setLoading] = useState(true);

  const canEdit = user && (user.role === 1 || (user.role === 2 && user.slug === id));

  const loadProfile = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);

      try {
        const res = await teacherService.getProfileByUserId(id);

        if (res.data) {
          setTeacher(mapProfileToTeacher(res.data));
        } else if (canEdit && user?.role === 2) {
          setShowStepper(true);
        } else {
          navigate('/');
        }
      } catch (err) {
        if (err.response?.status === 404 && canEdit && user?.role === 2) {
          setShowStepper(true);
        } else if (!silent) {
          navigate('/');
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [id, user, navigate, canEdit],
  );

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleProfileUpdate = (updatedProfile) => {
    setTeacher(mapProfileToTeacher(updatedProfile.data ?? updatedProfile));
  };

  const handleReviewSubmitted = async () => {
    await loadProfile({ silent: true });
  };

  useEffect(() => {
    const teacherUserId = teacher?.userId ?? teacher?.id ?? null;

    if (!teacherUserId) {
      setTeacherFees([]);
      setFeesLoading(false);
      setFeesError('');
      return undefined;
    }

    let cancelled = false;

    const loadTeacherFees = async () => {
      setFeesLoading(true);
      setFeesError('');

      try {
        const fees = await fetchTeacherFees(teacherUserId);

        if (!cancelled) {
          setTeacherFees(fees);
        }
      } catch (error) {
        if (!cancelled) {
          setTeacherFees([]);
          setFeesError('Unable to load teacher fees right now.');
        }
      } finally {
        if (!cancelled) {
          setFeesLoading(false);
        }
      }
    };

    loadTeacherFees();

    return () => {
      cancelled = true;
    };
  }, [teacher?.id, teacher?.userId]);

  const sections = [
    { selector: '#headerSection', imagePosition: 'left' },
    { selector: '#overviewSection', imagePosition: 'center' },
    { selector: '#timeSlotSection', imagePosition: 'right' },
    { selector: '#recommendationsSection', imagePosition: 'center' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // No profile yet — stepper handles creation
  if (!teacher && showStepper) {
    return (
      <TeacherStepperModal
        open={showStepper}
        user={user}
        onComplete={(data) => {
          setTeacher(mapProfileToTeacher(data.data ?? data));
          setShowStepper(false);
        }}
      />
    );
  }

  if (!teacher) return null;

  return (
    <div className="min-h-screen">
      <AnimatedBlobBackground sections={sections} />

      {/* Header */}
      <section id="headerSection">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 grid-cols-1 gap-10">
            <div className="col-span-1">
              <ProfileHeader teacher={teacher} canEdit={canEdit} onUpdate={handleProfileUpdate} />
            </div>
            <div className="col-span-1">
              <AboutSection teacher={teacher} canEdit={canEdit} onUpdate={handleProfileUpdate} />
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="mt-10" id="overviewSection">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 grid-cols-1 gap-10">
            <div className="col-span-1">
              <OverviewSection
                teacher={teacher}
                canEdit={canEdit}
                onUpdate={handleProfileUpdate}
                teacherFees={teacherFees}
                feesLoading={feesLoading}
                feesError={feesError}
              />
            </div>
            <div className="col-span-1">
              <QualificationSection
                teacher={teacher}
                canEdit={canEdit}
                onUpdate={handleProfileUpdate}
              />
            </div>
          </div>

        </div>
      </section>

      {/* Time Slots + Reviews */}
      <section className="mt-10" id="timeSlotSection">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 grid-cols-1 gap-10">
            <div className="col-span-1">
              <TimeSlotsSection
                teacher={teacher}
                canEdit={canEdit}
                onUpdate={handleProfileUpdate}
              />
            </div>
            <div className="col-span-1">
              <ReviewsSection teacher={teacher} onReviewSubmitted={handleReviewSubmitted} />
            </div>
          </div>
        </div>
      </section>

      {/* Recommendations */}
      <section className="mt-10" id="recommendationsSection">
        <div className="container mx-auto">
          <RecommendationsSection teacher={teacher} />
        </div>
      </section>
    </div>
  );
}
